/**
 * Triggers Word-draft generation on signus-service and polls for the
 * finished document. The backend produces a fully-formatted .docx (not a
 * structured text shape), returned as base64 once the async generation
 * session completes.
 */

import { SIGNUS_API_URL } from './constants';
import { ApiError, extractErrorMessage, extractErrorType, fetchWithTokenRefresh } from './apiFetch';

export interface TextSpan {
  text: string;
  style: 'normal' | 'bold';
}

export interface QuestionAnswer {
  question: string;
  answer: string;
}

export interface DraftGenerationResult {
  filename: string;
  generatedDocumentId: string;
  summarySpans: TextSpan[];
  contentBase64: string;
  contentType: string;
}

type DraftGenerationStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED';

/** Starts an async draft-generation session and returns its id. */
export async function triggerDraftGeneration(
  teamSlug: string,
  accessToken: string,
  questionsAndAnswers: QuestionAnswer[],
): Promise<string> {
  const url = `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/draft-generation`;

  const res = await fetchWithTokenRefresh(url, accessToken, token => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ questionsAndAnswers }),
  }));

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      extractErrorMessage(
        json,
        `Failed to start draft generation: ${res.status} ${res.statusText}`,
      ),
      extractErrorType(json),
    );
  }

  const generationId: string | undefined = json?.payload?.generationId;
  if (!generationId) {
    throw new Error('signus-service did not return a generationId.');
  }
  return generationId;
}

async function fetchDraftGenerationStatus(
  teamSlug: string,
  accessToken: string,
  generationId: string,
): Promise<{
  status: DraftGenerationStatus;
  errorMessage?: string;
  result: DraftGenerationResult | null;
}> {
  const url = `${SIGNUS_API_URL}/v0/word/teams/${encodeURIComponent(teamSlug)}/draft/generate/${encodeURIComponent(generationId)}/result`;

  const res = await fetchWithTokenRefresh(url, accessToken, token => ({
    headers: { Authorization: token },
  }));

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(
      extractErrorMessage(
        json,
        `Failed to fetch generation status: ${res.status} ${res.statusText}`,
      ),
    );
  }

  const payload = json?.payload ?? {};
  return {
    status: payload.status,
    errorMessage: payload.errorMessage,
    result: payload.result ?? null,
  };
}

const POLL_INTERVAL_MS = 2000;
// Matches the Temporal session's own idle timeout on the backend — no point
// polling longer than the session can possibly stay alive for.
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Generation cancelled.'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('Generation cancelled.'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Polls the generation-result endpoint until the session completes, fails,
 * or times out. `signal` (tied to component unmount) aborts an in-flight
 * sleep immediately so callers don't leak a dangling poll loop.
 */
export async function pollDraftGenerationResult(
  teamSlug: string,
  accessToken: string,
  generationId: string,
  signal?: AbortSignal,
): Promise<DraftGenerationResult> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  for (;;) {
    const { status, errorMessage, result } = await fetchDraftGenerationStatus(
      teamSlug,
      accessToken,
      generationId,
    );

    if (status === 'COMPLETED' && result) return result;
    if (status === 'FAILED') throw new Error(errorMessage || 'Draft generation failed.');
    if (Date.now() >= deadline) throw new Error('Draft generation timed out. Please try again.');

    await sleep(POLL_INTERVAL_MS, signal);
  }
}
